import React, { useEffect } from 'react';
import { useState } from 'react';
import { videoPlayerConfig } from '../config/videoPlayer';
import { songData, featuredSongsOrder } from '../data/songs';
import { Song } from '../types';
import { extractVideoId } from '../utils/youtubeUtils';
import '../styles/components/VideoPlayer.css';

interface VideoPlayerProps {
  isPartyModeActive?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ isPartyModeActive = false }) => {
    const [currentVideo, setCurrentVideo] = useState<number>(0);
    const [thumbnailStartIndex, setThumbnailStartIndex] = useState<number>(0);
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const { videosPerPage, animationDuration, thumbnailMinWidth, thumbnailHeight } = videoPlayerConfig;

    // Set CSS custom properties based on config
    useEffect(() => {
        const videoContainer = document.querySelector('.video-container') as HTMLElement;
        if (videoContainer) {
            videoContainer.style.setProperty('--videos-per-page', videosPerPage.toString());
            videoContainer.style.setProperty('--animation-duration', `${animationDuration}ms`);
            videoContainer.style.setProperty('--thumbnail-min-width', `${thumbnailMinWidth}px`);
            videoContainer.style.setProperty('--thumbnail-height', `${thumbnailHeight}px`);
        }
    }, [videosPerPage, animationDuration, thumbnailMinWidth, thumbnailHeight]);

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Note: Party mode video is handled by PartyModeVideo component

    // Filter songs that have videos and sort by featured order
    const songsWithVideos: Song[] = (() => {
        const allSongsWithVideos = songData.filter(song => song.videoUrl);
        
        // Separate featured songs and other songs
        const featuredSongs: Song[] = [];
        const otherSongs: Song[] = [];
        
        allSongsWithVideos.forEach(song => {
            if (featuredSongsOrder.includes(song.title)) {
                featuredSongs.push(song);
            } else {
                otherSongs.push(song);
            }
        });
        
        // Sort featured songs by the order in featuredSongsOrder array
        const sortedFeaturedSongs = featuredSongsOrder
            .map(title => featuredSongs.find(song => song.title === title))
            .filter((song): song is Song => song !== undefined);
        
        // Sort other songs alphabetically by title
        const sortedOtherSongs = [...otherSongs].sort((a, b) => a.title.localeCompare(b.title));
        
        // Combine: featured songs first, then alphabetically sorted other songs
        return [...sortedFeaturedSongs, ...sortedOtherSongs];
    })();
    
    // Convert to video format for the player
    const videos = songsWithVideos.map(song => ({
        id: extractVideoId(song.videoUrl!),
        title: `${song.title} - ${song.artist}`
    }));

    const handleVideoSelect = (index: number): void => {
        setCurrentVideo(index);
    };

    const handlePreviousThumbnails = (): void => {
        setThumbnailStartIndex(Math.max(0, thumbnailStartIndex - videosPerPage));
    };

    const handleNextThumbnails = (): void => {
        setThumbnailStartIndex(Math.min(videos.length - videosPerPage, thumbnailStartIndex + videosPerPage));
    };


    const canGoPrevious = thumbnailStartIndex > 0;
    const canGoNext = thumbnailStartIndex + videosPerPage < videos.length;
    
    // Calculate the transform value - each page moves by 100% of container width
    // Don't apply transform on mobile since we show all videos in a grid
    const transformValue = isMobile ? 0 : (thumbnailStartIndex / videosPerPage) * 100;

  return (
    <>
    <div className="video-container">
        {/* Main Video Player */}
        <div className="video-wrapper">
        <iframe 
            id="main-video-player"
            key={currentVideo}
            src={`https://www.youtube.com/embed/${videos[currentVideo]?.id || ''}`}
            title={videos[currentVideo]?.title || ''}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen>
        </iframe>
        </div>
        
        {/* Video Selection - Different layouts for mobile and desktop */}
        <div className="video-gallery">
        {isMobile ? (
            /* Mobile: Simple text pills */
            <div className="mobile-video-pills">
                {videos.map((video, index) => (
                    <button
                        key={index}
                        className={`video-pill ${index === currentVideo ? 'active' : ''}`}
                        onClick={() => handleVideoSelect(index)}
                    >
                        {video.title}
                    </button>
                ))}
            </div>
        ) : (
            /* Desktop: Thumbnail grid with navigation */
            <>
                <div className="thumbnail-navigation">
                    <button 
                    className={`nav-arrow nav-arrow-left ${!canGoPrevious ? 'disabled' : ''}`}
                    onClick={handlePreviousThumbnails}
                    disabled={!canGoPrevious}
                    aria-label="Previous videos"
                    >
                    ‹
                    </button>
                    
                    <div className="thumbnail-grid-container">
                        <div 
                            className="thumbnail-grid"
                            style={{
                                transform: `translateX(-${transformValue}%)`,
                                transition: `transform ${animationDuration}ms ease-in-out`
                            }}
                        >
                        {videos.map((video, index) => (
                            <div 
                                key={index}
                                className={`thumbnail-item ${index === currentVideo ? 'active' : ''}`}
                                data-video-id={video.id}
                                data-video-title={video.title}
                                onClick={() => handleVideoSelect(index)}
                            >
                                <img 
                                src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`} 
                                alt={video.title} 
                                className="thumbnail-image" 
                                />
                                <div className="thumbnail-overlay">
                                <div className="play-icon">▶</div>
                                </div>
                                <div className="thumbnail-title">
                                {video.title}
                                </div>
                            </div>
                        ))}
                        </div>
                    </div>
                    
                    <button 
                    className={`nav-arrow nav-arrow-right ${!canGoNext ? 'disabled' : ''}`}
                    onClick={handleNextThumbnails}
                    disabled={!canGoNext}
                    aria-label="Next videos"
                    >
                    ›
                    </button>
                </div>
                
                <div className="thumbnail-pagination">
                    <span className="pagination-info">
                    Showing {thumbnailStartIndex + 1}-{Math.min(thumbnailStartIndex + videosPerPage, videos.length)} of {videos.length} videos
                    </span>
                </div>
            </>
        )}
        </div>
    </div>
    </>
  );
};

export default VideoPlayer;
